import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Empty,
    Image,
    Input,
    Modal,
    Progress,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tabs,
    Tag,
    Typography,
    message
} from 'antd';
import {
    CalendarOutlined,
    CheckCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    FileImageOutlined,
    PlusOutlined,
    ReloadOutlined,
    SettingOutlined,
    StopOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import {
    deleteMedicSchedule,
    deleteScheduleException,
    getMedicScheduleByUser,
    getOrganizationManagementCenters,
    getOrganizationManagementPlan,
    getOrganizationMedics,
    getReasonsScheduleExceptions,
    getWeekDays,
    postMedicSchedule,
    postScheduleException,
    putOrganization,
    putMedicSchedule,
    putScheduleException
} from '../store/user/thunks';
import OrganizationCentersManagement from './OrganizationCentersManagement';
import OrganizationCenterSpecialityManagement from './OrganizationCenterSpecialityManagement';
import OrganizationSpecialityManagement from './OrganizationSpecialityManagement';
import GlobalOrganizationConfiguration from './GlobalOrganizationConfiguration';
import OrganizationBusinessCoverageManagement from './OrganizationBusinessCoverageManagement';

const { Title, Text } = Typography;

type AnyRecord = Record<string, any>;

interface OrganizationSettingsProps {
    organizationId: string;
    organizationName?: string;
    organizationLogoUrl?: string | null;
    organizationLogoMedicalOrderUrl?: string | null;
    onOrganizationLogosUpdated?: (organizationId: string, logos: { logoUrl?: string; logoMedicalOrderUrl?: string }) => void;
    getOrganizationMedics?: any;
    getOrganizationManagementCenters?: any;
    getOrganizationManagementPlan?: any;
    getWeekDays?: any;
    getMedicScheduleByUser?: any;
    getReasonsScheduleExceptions?: any;
    putOrganization?: any;
    postMedicSchedule?: any;
    putMedicSchedule?: any;
    deleteMedicSchedule?: any;
    postScheduleException?: any;
    putScheduleException?: any;
    deleteScheduleException?: any;
}

interface WeekDay {
    id: string;
    name: string;
}

interface Center {
    id: string;
    name: string;
    status?: string;
}

interface Medic {
    id: string;
    fullName: string;
    email?: string;
}

interface ScheduleRow {
    clientKey: string;
    id?: string;
    draftId?: string;
    centerId?: string;
    centerName?: string;
    weekDaysId?: string;
    weekDayName?: string;
    type: string;
    startHour: string;
    endHour: string;
    minutes: number;
}

interface ScheduleReason {
    id: string;
    name: string;
}

interface ScheduleExceptionRow {
    clientKey: string;
    id?: string;
    exceptionType: 'COMPLETO' | 'PARCIAL';
    exceptionDate?: string;
    startTime?: string;
    endTime?: string;
    reasonsScheduleExceptionsId?: string;
    reasonName?: string;
    observation?: string;
    weekDaysId?: string;
    weekDayName?: string;
}

const weekOrder = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];

const pick = (source: AnyRecord | null | undefined, ...keys: string[]) => {
    if (!source) return undefined;
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null) return source[key];
    }
    return undefined;
};

const normalizeName = (value?: string) => (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const displayTime = (value?: string) => (value || '').slice(0, 5);

const apiTime = (value?: string) => {
    const normalized = displayTime(value);
    return normalized ? `${normalized}:00` : '';
};

const fileToBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const result = String(reader.result || '');
        resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

const formatDate = (value?: string) => {
    if (!value) return 'No disponible';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('es-EC');
};

const formatExceptionDate = (value?: string) => value ? value.split('-').reverse().join('/') : 'Recurrente';

const normalizeWeekDays = (source: AnyRecord | AnyRecord[] | null | undefined): WeekDay[] => {
    const values = Array.isArray(source) ? source : pick(source, 'weekDays', 'weekdays', 'data');
    return Array.isArray(values)
        ? values.map((item: AnyRecord) => ({
            id: String(pick(item, 'id', 'Id') || ''),
            name: String(pick(item, 'name', 'Name') || '')
        })).filter(item => item.id && item.name)
        : [];
};

const normalizeSchedules = (source: AnyRecord | null | undefined): ScheduleRow[] => {
    const values = pick(source, 'schedules', 'Schedules');
    return Array.isArray(values)
        ? values.map((item: AnyRecord, index: number) => {
            const id = pick(item, 'id', 'Id') ? String(pick(item, 'id', 'Id')) : undefined;
            return {
                clientKey: id ? `server-${id}-${index}` : `server-row-${index}`,
                id,
                centerId: pick(item, 'centerId', 'CenterId') ? String(pick(item, 'centerId', 'CenterId')) : undefined,
                centerName: pick(item, 'centerName', 'CenterName'),
                weekDaysId: pick(item, 'weekDaysId', 'WeekDaysId') ? String(pick(item, 'weekDaysId', 'WeekDaysId')) : undefined,
                weekDayName: pick(item, 'weekDayName', 'WeekDayName'),
                type: String(pick(item, 'type', 'Type') || 'PRESENCIAL'),
                startHour: displayTime(String(pick(item, 'startHour', 'StartHour') || '08:00')),
                endHour: displayTime(String(pick(item, 'endHour', 'EndHour') || '17:00')),
                minutes: Number(pick(item, 'minutes', 'Minutes') || 30)
            };
        })
        : [];
};

const normalizeScheduleExceptions = (source: AnyRecord | null | undefined): ScheduleExceptionRow[] => {
    const values = pick(source, 'exceptions', 'Exceptions');
    return Array.isArray(values)
        ? values.map((item: AnyRecord, index: number) => {
            const id = pick(item, 'id', 'Id') ? String(pick(item, 'id', 'Id')) : undefined;
            const exceptionType = String(pick(item, 'exceptionType', 'ExceptionType') || 'COMPLETO').toUpperCase();
            return {
                clientKey: id ? `exception-${id}-${index}` : `exception-row-${index}`,
                id,
                exceptionType: exceptionType === 'PARCIAL' ? 'PARCIAL' : 'COMPLETO',
                exceptionDate: pick(item, 'exceptionDate', 'ExceptionDate') ? String(pick(item, 'exceptionDate', 'ExceptionDate')).slice(0, 10) : undefined,
                startTime: pick(item, 'startTime', 'StartTime') ? displayTime(String(pick(item, 'startTime', 'StartTime'))) : undefined,
                endTime: pick(item, 'endTime', 'EndTime') ? displayTime(String(pick(item, 'endTime', 'EndTime'))) : undefined,
                reasonsScheduleExceptionsId: pick(item, 'reasonsScheduleExceptionsId', 'ReasonsScheduleExceptionsId') ? String(pick(item, 'reasonsScheduleExceptionsId', 'ReasonsScheduleExceptionsId')) : undefined,
                reasonName: pick(item, 'reasonName', 'ReasonName'),
                observation: pick(item, 'observation', 'Observation'),
                weekDaysId: pick(item, 'weekDaysId', 'WeekDaysId') ? String(pick(item, 'weekDaysId', 'WeekDaysId')) : undefined,
                weekDayName: pick(item, 'weekDayName', 'WeekDayName')
            };
        })
        : [];
};

const weekDayNamesByIndex = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

const normalizeMedics = (source: AnyRecord | AnyRecord[] | null | undefined): Medic[] => {
    const values = Array.isArray(source) ? source : pick(source, 'medics', 'users', 'data');
    return Array.isArray(values)
        ? values.map((item: AnyRecord) => ({
            id: String(pick(item, 'id', 'userId', 'Id', 'UserId') || ''),
            fullName: String(pick(item, 'fullName', 'name', 'FullName', 'Name') || 'Profesional'),
            email: pick(item, 'email', 'Email')
        })).filter(item => item.id)
        : [];
};

const normalizeCenters = (source: AnyRecord | AnyRecord[] | null | undefined): Center[] => {
    const values = Array.isArray(source) ? source : pick(source, 'centers', 'data');
    return Array.isArray(values)
        ? values.map((item: AnyRecord) => ({
            id: String(pick(item, 'id', 'Id') || ''),
            name: String(pick(item, 'name', 'Name') || 'Centro'),
            status: pick(item, 'status', 'Status')
        })).filter(item => item.id)
        : [];
};

const OrganizationSettingsManagement: React.FC<OrganizationSettingsProps> = props => {
    const {
        organizationId,
        getOrganizationMedics,
        getOrganizationManagementCenters,
        getOrganizationManagementPlan,
        getWeekDays,
        getMedicScheduleByUser,
        getReasonsScheduleExceptions
    } = props;
    const [organizationLogoUrl, setOrganizationLogoUrl] = useState<string | undefined>(props.organizationLogoUrl || undefined);
    const [organizationLogoMedicalOrderUrl, setOrganizationLogoMedicalOrderUrl] = useState<string | undefined>(props.organizationLogoMedicalOrderUrl || undefined);
    const [organizationPlan, setOrganizationPlan] = useState<AnyRecord | null>(null);
    const [medics, setMedics] = useState<Medic[]>([]);
    const [centers, setCenters] = useState<Center[]>([]);
    const [weekDays, setWeekDays] = useState<WeekDay[]>([]);
    const [scheduleReasons, setScheduleReasons] = useState<ScheduleReason[]>([]);
    const [selectedMedicId, setSelectedMedicId] = useState<string>();
    const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
    const [scheduleExceptions, setScheduleExceptions] = useState<ScheduleExceptionRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [savingId, setSavingId] = useState<string>();
    const [exceptionModalOpen, setExceptionModalOpen] = useState(false);
    const [editingExceptionId, setEditingExceptionId] = useState<string>();
    const [exceptionType, setExceptionType] = useState<'COMPLETO' | 'PARCIAL'>('COMPLETO');
    const [exceptionDate, setExceptionDate] = useState('');
    const [exceptionWeekDayId, setExceptionWeekDayId] = useState<string>();
    const [exceptionReasonId, setExceptionReasonId] = useState<string>();
    const [exceptionStartTime, setExceptionStartTime] = useState('08:00');
    const [exceptionEndTime, setExceptionEndTime] = useState('17:00');
    const [exceptionObservation, setExceptionObservation] = useState('');
    const [exceptionSaving, setExceptionSaving] = useState(false);
    const [uploadingAsset, setUploadingAsset] = useState<string>();

    useEffect(() => {
        setOrganizationLogoUrl(props.organizationLogoUrl || undefined);
        setOrganizationLogoMedicalOrderUrl(props.organizationLogoMedicalOrderUrl || undefined);
    }, [organizationId, props.organizationLogoMedicalOrderUrl, props.organizationLogoUrl]);

    const loadSchedule = useCallback(async (medicId: string) => {
        setScheduleLoading(true);
        try {
            const response = await getMedicScheduleByUser?.(medicId, organizationId);
            setSchedules(normalizeSchedules(response));
            setScheduleExceptions(normalizeScheduleExceptions(response));
        } finally {
            setScheduleLoading(false);
        }
    }, [getMedicScheduleByUser, organizationId]);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setSelectedMedicId(undefined);
        setSchedules([]);
        setScheduleExceptions([]);

        Promise.all([
            getOrganizationMedics?.(organizationId),
            getOrganizationManagementCenters?.(organizationId),
            getOrganizationManagementPlan?.(organizationId),
            getWeekDays?.(),
            getReasonsScheduleExceptions?.()
        ]).then(([medicsResponse, centersResponse, planResponse, weekDaysResponse, reasonsResponse]) => {
            if (!active) return;
            const nextMedics = normalizeMedics(medicsResponse);
            const preferredMedicId = nextMedics[0]?.id;

            setOrganizationPlan(planResponse && !Array.isArray(planResponse) ? planResponse as AnyRecord : null);
            setMedics(nextMedics);
            setCenters(normalizeCenters(centersResponse));
            setWeekDays(normalizeWeekDays(weekDaysResponse));
            const reasonValues = Array.isArray(reasonsResponse) ? reasonsResponse : pick(reasonsResponse, 'data', 'Data', 'reasons', 'Reasons');
            setScheduleReasons(Array.isArray(reasonValues) ? reasonValues.map((item: AnyRecord) => ({
                id: String(pick(item, 'id', 'Id') || ''),
                name: String(pick(item, 'name', 'Name') || '')
            })).filter(item => item.id && item.name) : []);
            setSelectedMedicId(preferredMedicId);
            if (!preferredMedicId) setSchedules([]);
        }).finally(() => {
            if (active) setLoading(false);
        });

        return () => {
            active = false;
        };
    }, [organizationId, getOrganizationMedics, getOrganizationManagementCenters, getOrganizationManagementPlan, getWeekDays, getReasonsScheduleExceptions]);

    useEffect(() => {
        if (selectedMedicId) loadSchedule(selectedMedicId);
    }, [selectedMedicId, loadSchedule]);

    const sortedWeekDays = useMemo(() => [...weekDays].sort((left, right) => {
        const leftIndex = weekOrder.indexOf(normalizeName(left.name));
        const rightIndex = weekOrder.indexOf(normalizeName(right.name));
        return (leftIndex < 0 ? 99 : leftIndex) - (rightIndex < 0 ? 99 : rightIndex);
    }), [weekDays]);

    const getRowsForDay = useCallback((day: WeekDay) => schedules.filter(row =>
        row.weekDaysId === day.id || normalizeName(row.weekDayName) === normalizeName(day.name)
    ), [schedules]);

    const capacityByDay = useMemo(() => sortedWeekDays.map(day => {
        const total = getRowsForDay(day).reduce((sum, row) => {
            const start = Number(row.startHour.split(':')[0]) * 60 + Number(row.startHour.split(':')[1] || 0);
            const end = Number(row.endHour.split(':')[0]) * 60 + Number(row.endHour.split(':')[1] || 0);
            const duration = row.minutes || 30;
            return sum + (end > start ? Math.floor((end - start) / duration) : 0);
        }, 0);
        return { ...day, total };
    }), [getRowsForDay, sortedWeekDays]);

    const totalCapacity = capacityByDay.reduce((sum, day) => sum + day.total, 0);

    const updateRow = (row: ScheduleRow, changes: Partial<ScheduleRow>) => {
        setSchedules(current => current.map(item => item.clientKey === row.clientKey ? { ...item, ...changes } : item));
    };

    const addSchedule = (day: WeekDay) => {
        const existing = getRowsForDay(day)[0];
        const draftId = `${day.id}-${Date.now()}`;
        setSchedules(current => [...current, {
            clientKey: `draft-${draftId}`,
            draftId,
            centerId: existing?.centerId || centers[0]?.id,
            centerName: existing?.centerName || centers[0]?.name,
            weekDaysId: day.id,
            weekDayName: day.name,
            type: 'PRESENCIAL',
            startHour: '08:00',
            endHour: '17:00',
            minutes: 30
        }]);
    };

    const saveSchedule = async (row: ScheduleRow) => {
        if (!selectedMedicId || !row.weekDaysId || !row.centerId || !row.startHour || !row.endHour) {
            message.warning('Complete día, centro y rango horario antes de guardar.');
            return;
        }
        const key = row.clientKey;
        if (key) setSavingId(key);
        try {
            const payload = {
                CenterId: row.centerId,
                WeekDaysId: row.weekDaysId,
                Type: row.type,
                StartHour: apiTime(row.startHour),
                EndHour: apiTime(row.endHour),
                Minutes: row.minutes
            };
            const response = row.id
                ? await props.putMedicSchedule?.({ Id: row.id, ...payload })
                : await props.postMedicSchedule?.({ UserId: selectedMedicId, Schedules: [payload] });
            if (response) await loadSchedule(selectedMedicId);
        } finally {
            setSavingId(undefined);
        }
    };

    const removeSchedule = async (row: ScheduleRow) => {
        if (!row.id) {
            setSchedules(current => current.filter(item => item.clientKey !== row.clientKey));
            return;
        }
        const response = await props.deleteMedicSchedule?.(row.id);
        if (response && selectedMedicId) await loadSchedule(selectedMedicId);
    };

    const resetExceptionForm = () => {
        const monday = weekDays.find(day => normalizeName(day.name) === 'lunes') || weekDays[0];
        setEditingExceptionId(undefined);
        setExceptionType('COMPLETO');
        setExceptionDate('');
        setExceptionWeekDayId(monday?.id);
        setExceptionReasonId(scheduleReasons[0]?.id);
        setExceptionStartTime('08:00');
        setExceptionEndTime('17:00');
        setExceptionObservation('');
    };

    const openNewException = () => {
        resetExceptionForm();
        setExceptionModalOpen(true);
    };

    const openEditException = (exception: ScheduleExceptionRow) => {
        setEditingExceptionId(exception.id);
        setExceptionType(exception.exceptionType);
        setExceptionDate(exception.exceptionDate || '');
        setExceptionWeekDayId(exception.weekDaysId);
        setExceptionReasonId(exception.reasonsScheduleExceptionsId);
        setExceptionStartTime(exception.startTime || '08:00');
        setExceptionEndTime(exception.endTime || '17:00');
        setExceptionObservation(exception.observation || '');
        setExceptionModalOpen(true);
    };

    const handleExceptionDateChange = (value: string) => {
        setExceptionDate(value);
        if (!value) return;
        const date = new Date(`${value}T00:00:00`);
        const dayName = weekDayNamesByIndex[date.getDay()];
        const matchingDay = weekDays.find(day => normalizeName(day.name) === normalizeName(dayName));
        if (matchingDay) setExceptionWeekDayId(matchingDay.id);
    };

    const saveException = async () => {
        if (!selectedMedicId || !exceptionWeekDayId || !exceptionReasonId) {
            message.warning('Complete el día y el motivo del bloqueo.');
            return;
        }
        if (exceptionType === 'PARCIAL' && (!exceptionStartTime || !exceptionEndTime || exceptionStartTime >= exceptionEndTime)) {
            message.warning('Defina un rango de horas válido para el bloqueo parcial.');
            return;
        }

        setExceptionSaving(true);
        try {
            const commonPayload = {
                ExceptionType: exceptionType,
                ExceptionDate: exceptionDate || null,
                StartTime: exceptionType === 'PARCIAL' ? apiTime(exceptionStartTime) : null,
                EndTime: exceptionType === 'PARCIAL' ? apiTime(exceptionEndTime) : null,
                ReasonsScheduleExceptionsId: exceptionReasonId,
                Observation: exceptionObservation.trim() || null,
                WeekDaysId: exceptionWeekDayId
            };
            const response = editingExceptionId
                ? await props.putScheduleException?.({ Id: editingExceptionId, ...commonPayload })
                : await props.postScheduleException?.({ UserId: selectedMedicId, ...commonPayload });
            if (response) {
                setExceptionModalOpen(false);
                await loadSchedule(selectedMedicId);
            }
        } finally {
            setExceptionSaving(false);
        }
    };

    const removeException = async (exception: ScheduleExceptionRow) => {
        if (!exception.id) return;
        const response = await props.deleteScheduleException?.(exception.id);
        if (response && selectedMedicId) await loadSchedule(selectedMedicId);
    };

    const uploadLogo = async (event: React.ChangeEvent<HTMLInputElement>, asset: 'profile' | 'medical') => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        const maximumBytes = asset === 'profile' ? 5 * 1024 * 1024 : 3 * 1024 * 1024;
        if (file.type !== 'image/png') {
            message.warning('El logo debe estar en formato PNG.');
            return;
        }
        if (file.size > maximumBytes) {
            message.warning(`El archivo supera el máximo de ${asset === 'profile' ? 5 : 3} MB.`);
            return;
        }
        setUploadingAsset(asset);
        try {
            const base64 = await fileToBase64(file);
            const response = await props.putOrganization?.({
                Id: props.organizationId,
                Name: props.organizationName || organizationDisplayName,
                ...(asset === 'profile' ? { Logo: base64 } : { LogoMedicalOrder: base64 })
            });
            if (response) {
                const updatedLogoUrl = pick(response, 'logoUrl', 'LogoUrl');
                const updatedLogoMedicalOrderUrl = pick(response, 'logoMedicalOrderUrl', 'LogoMedicalOrderUrl');
                if (asset === 'profile' && updatedLogoUrl) {
                    const logoUrl = `${updatedLogoUrl}?v=${Date.now()}`;
                    setOrganizationLogoUrl(logoUrl);
                    props.onOrganizationLogosUpdated?.(props.organizationId, { logoUrl });
                }
                if (asset === 'medical' && updatedLogoMedicalOrderUrl) {
                    const logoMedicalOrderUrl = `${updatedLogoMedicalOrderUrl}?v=${Date.now()}`;
                    setOrganizationLogoMedicalOrderUrl(logoMedicalOrderUrl);
                    props.onOrganizationLogosUpdated?.(props.organizationId, { logoMedicalOrderUrl });
                }
            }
        } finally {
            setUploadingAsset(undefined);
        }
    };

    const contract = organizationPlan as AnyRecord | null;
    const flags = (contract && pick(contract, 'featureFlagsResponse', 'FeatureFlagsResponse')) as AnyRecord | null;
    const contractRoles = (contract && pick(contract, 'rolSystems', 'RolSystems')) as AnyRecord[] | null;
    const organizationDisplayName = String(props.organizationName || 'Organización');

    const organizationAssetsSection = (
        <Card title={<Space><FileImageOutlined /> Imágenes de la organización</Space>}>
            <Row gutter={[16, 16]}>
                {[
                    { key: 'profile', label: 'Logo de perfil', url: organizationLogoUrl, description: 'PNG máximo 5 MB. Se muestra como imagen de la organización.' },
                    { key: 'medical', label: 'Logo para recetas', url: organizationLogoMedicalOrderUrl, description: 'PNG máximo 3 MB. Se usa en recetas y órdenes médicas.' }
                ].map(asset => (
                    <Col xs={24} md={12} key={asset.key}>
                        <Card size="small">
                            <Space align="start">
                                {asset.url ? <Image width={72} height={72} style={{ objectFit: 'contain' }} src={asset.url as string} /> : <Avatar shape="square" size={72} icon={<FileImageOutlined />} />}
                                <div>
                                    <Text strong>{asset.label}</Text>
                                    <br />
                                    <Text type="secondary">{asset.url ? 'Imagen configurada' : 'Sin imagen configurada'}</Text>
                                    <br />
                                    <Text type="secondary">{asset.description}</Text>
                                </div>
                            </Space>
                            <label htmlFor={`organization-logo-${asset.key}`} style={{ display: 'block', marginTop: 16 }}>
                                <Button block type="primary" icon={<FileImageOutlined />} loading={uploadingAsset === asset.key}>
                                    Cambiar PNG
                                </Button>
                            </label>
                            <input
                                id={`organization-logo-${asset.key}`}
                                type="file"
                                accept="image/png"
                                style={{ display: 'none' }}
                                onChange={event => uploadLogo(event, asset.key as 'profile' | 'medical')}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>
            <Alert style={{ marginTop: 16 }} type="info" showIcon message="Estas imágenes son globales para la organización seleccionada." />
        </Card>
    );

    const exceptionSection = (
        <Card
            title={<Space><StopOutlined /> Bloqueos de agenda</Space>}
            extra={<Button type="primary" icon={<PlusOutlined />} onClick={openNewException} disabled={!selectedMedicId || !scheduleReasons.length}>Bloquear agenda</Button>}
        >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Text type="secondary">Bloquea un día completo o un rango de horas para el profesional seleccionado.</Text>
                {!scheduleReasons.length && <Alert type="warning" showIcon message="No hay motivos de bloqueo configurados en el sistema." />}
                {!!scheduleReasons.length && !scheduleExceptions.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay bloqueos registrados" />}
                {scheduleExceptions.map(exception => {
                    const weekDay = exception.weekDayName || weekDays.find(day => day.id === exception.weekDaysId)?.name || 'Día no disponible';
                    const range = exception.exceptionType === 'COMPLETO'
                        ? 'Día completo'
                        : `${exception.startTime || '--:--'} - ${exception.endTime || '--:--'}`;
                    return <Card key={exception.clientKey} size="small">
                        <Row gutter={[12, 12]} align="middle">
                            <Col xs={24} md={6}>
                                <Text strong>{formatExceptionDate(exception.exceptionDate)}</Text>
                                <br />
                                <Text type="secondary">{weekDay}</Text>
                            </Col>
                            <Col xs={24} md={6}>
                                <Tag color={exception.exceptionType === 'COMPLETO' ? 'red' : 'orange'}>
                                    {exception.exceptionType === 'COMPLETO' ? 'Día bloqueado' : 'Horas bloqueadas'}
                                </Tag>
                                <br />
                                <Text>{range}</Text>
                            </Col>
                            <Col xs={24} md={7}>
                                <Text strong>{exception.reasonName || 'Motivo no disponible'}</Text>
                                {exception.observation && <><br /><Text type="secondary">{exception.observation}</Text></>}
                            </Col>
                            <Col xs={24} md={5} style={{ textAlign: 'right' }}>
                                <Space>
                                    <Button icon={<EditOutlined />} onClick={() => openEditException(exception)}>Editar</Button>
                                    <Button danger icon={<DeleteOutlined />} onClick={() => removeException(exception)} />
                                </Space>
                            </Col>
                        </Row>
                    </Card>;
                })}
            </Space>
        </Card>
    );

    const exceptionModal = (
        <Modal
            title={editingExceptionId ? 'Editar bloqueo de agenda' : 'Bloquear agenda'}
            open={exceptionModalOpen}
            onCancel={() => setExceptionModalOpen(false)}
            onOk={saveException}
            okText={editingExceptionId ? 'Actualizar bloqueo' : 'Guardar bloqueo'}
            cancelText="Cancelar"
            confirmLoading={exceptionSaving}
            width={720}
        >
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
                <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}>
                        <Text type="secondary">Tipo de bloqueo</Text>
                        <Select
                            style={{ width: '100%' }}
                            value={exceptionType}
                            onChange={value => setExceptionType(value)}
                            options={[
                                { value: 'COMPLETO', label: 'Día completo' },
                                { value: 'PARCIAL', label: 'Rango de horas' }
                            ]}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <Text type="secondary">Motivo</Text>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Seleccione el motivo"
                            value={exceptionReasonId}
                            onChange={value => setExceptionReasonId(value)}
                            options={scheduleReasons.map(reason => ({ value: reason.id, label: reason.name }))}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <Text type="secondary">Fecha específica (opcional)</Text>
                        <Input type="date" value={exceptionDate} onChange={event => handleExceptionDateChange(event.target.value)} />
                    </Col>
                    <Col xs={24} md={12}>
                        <Text type="secondary">Día de la semana</Text>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Seleccione el día"
                            value={exceptionWeekDayId}
                            onChange={value => setExceptionWeekDayId(value)}
                            options={sortedWeekDays.map(day => ({ value: day.id, label: day.name }))}
                        />
                    </Col>
                    {exceptionType === 'PARCIAL' && <>
                        <Col xs={24} md={12}>
                            <Text type="secondary">Hora de inicio</Text>
                            <Input type="time" value={exceptionStartTime} onChange={event => setExceptionStartTime(event.target.value)} />
                        </Col>
                        <Col xs={24} md={12}>
                            <Text type="secondary">Hora de fin</Text>
                            <Input type="time" value={exceptionEndTime} onChange={event => setExceptionEndTime(event.target.value)} />
                        </Col>
                    </>}
                    <Col xs={24}>
                        <Text type="secondary">Observación</Text>
                        <Input.TextArea rows={3} value={exceptionObservation} onChange={event => setExceptionObservation(event.target.value)} placeholder="Detalle opcional del bloqueo" />
                    </Col>
                </Row>
                <Alert type="info" showIcon message="Sin fecha específica, el bloqueo se aplica de forma recurrente al día seleccionado." />
            </Space>
        </Modal>
    );

    const scheduleSection = (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card title={<Space><UserOutlined /> Usuarios médicos</Space>}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text type="secondary">Seleccione un médico para consultar y administrar sus horarios y bloqueos de agenda.</Text>
                    {!!medics.length && <Table
                        size="small"
                        rowKey="id"
                        pagination={false}
                        dataSource={medics}
                        columns={[
                            {
                                title: 'Médico',
                                dataIndex: 'fullName',
                                render: (value: string, row: Medic) => <Space direction="vertical" size={0}><Text strong>{value}</Text><Text type="secondary">{row.email || 'Sin correo registrado'}</Text></Space>
                            },
                            {
                                title: '',
                                key: 'actions',
                                align: 'right' as const,
                                render: (_: unknown, row: Medic) => <Button type={selectedMedicId === row.id ? 'primary' : 'default'} onClick={() => setSelectedMedicId(row.id)}>{selectedMedicId === row.id ? 'Seleccionado' : 'Configurar horario'}</Button>
                            }
                        ]}
                    />}
                </Space>
            </Card>
            <Card title={<Space><CalendarOutlined /> Horario</Space>} extra={selectedMedicId ? <Button icon={<ReloadOutlined />} onClick={() => loadSchedule(selectedMedicId)}>Actualizar</Button> : null}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text type="secondary">Activa los días y define los horarios por profesional.</Text>
                    <Select
                        style={{ minWidth: 280, maxWidth: '100%' }}
                        placeholder="Seleccione un profesional"
                        value={selectedMedicId}
                        onChange={value => setSelectedMedicId(value)}
                        options={medics.map(medic => ({ value: medic.id, label: medic.fullName }))}
                        disabled={!medics.length}
                    />
                </Space>
            </Card>
            {!medics.length && <Alert type="warning" showIcon message="No hay profesionales asociados a esta organización." />}
            {!!medics.length && <Card title="Capacidad instalada" extra={<Tag color="blue">{totalCapacity} pacientes</Tag>}>
                <Row gutter={[16, 16]}>
                    {capacityByDay.map(day => {
                        const percentage = totalCapacity ? Math.round(day.total * 100 / totalCapacity) : 0;
                        return <Col xs={12} sm={8} md={Math.max(3, Math.floor(24 / Math.max(capacityByDay.length, 1)))} key={day.id}>
                            <Statistic title={day.name} value={day.total} suffix="citas" />
                            <Progress percent={percentage} showInfo={false} strokeColor="#2864e8" />
                        </Col>;
                    })}
                </Row>
            </Card>}
            {exceptionSection}
            {scheduleLoading ? <Card><Spin /></Card> : sortedWeekDays.map(day => {
                const rows = getRowsForDay(day);
                return <Card
                    key={day.id}
                    title={<Space><span style={{ color: rows.length ? '#00a878' : '#bfbfbf' }}>●</span>{day.name}</Space>}
                    extra={<Button type="primary" shape="circle" icon={<PlusOutlined />} onClick={() => addSchedule(day)} disabled={!selectedMedicId} />}
                >
                    {!rows.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Día inactivo" />}
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        {rows.map(row => {
                            const key = row.clientKey;
                            return <Card size="small" key={key}>
                                <Row gutter={[8, 8]} align="bottom">
                                    <Col xs={24} sm={6} md={4}>
                                        <Text type="secondary">Inicio</Text>
                                        <Input type="time" value={row.startHour} onChange={event => updateRow(row, { startHour: event.target.value })} />
                                    </Col>
                                    <Col xs={24} sm={6} md={4}>
                                        <Text type="secondary">Fin</Text>
                                        <Input type="time" value={row.endHour} onChange={event => updateRow(row, { endHour: event.target.value })} />
                                    </Col>
                                    <Col xs={24} sm={8} md={5}>
                                        <Text type="secondary">Duración</Text>
                                        <Select style={{ width: '100%' }} value={row.minutes} onChange={value => updateRow(row, { minutes: value })} options={[15, 20, 30, 45, 60].map(value => ({ value, label: `${value} min` }))} />
                                    </Col>
                                    <Col xs={24} sm={10} md={5}>
                                        <Text type="secondary">Modalidad</Text>
                                        <Select style={{ width: '100%' }} value={row.type} onChange={value => updateRow(row, { type: value })} options={[{ value: 'PRESENCIAL', label: 'Presencial' }, { value: 'TELEMEDICINA', label: 'Telemedicina' }]} />
                                    </Col>
                                    <Col xs={24} sm={14} md={4}>
                                        <Text type="secondary">Centro</Text>
                                        <Select style={{ width: '100%' }} value={row.centerId} onChange={value => updateRow(row, { centerId: value, centerName: centers.find(center => center.id === value)?.name })} options={centers.map(center => ({ value: center.id, label: center.name }))} placeholder="Centro" />
                                    </Col>
                                    <Col xs={24} sm={10} md={2}>
                                        <Space>
                                            <Button type="primary" icon={<CheckCircleOutlined />} loading={savingId === key} onClick={() => saveSchedule(row)} />
                                            <Button danger icon={<DeleteOutlined />} onClick={() => removeSchedule(row)} />
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>;
                        })}
                    </Space>
                </Card>;
            })}
            {exceptionModal}
        </Space>
    );

    const planSection = (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card>
                <Space align="center">
                    <Avatar size={58} icon={<SettingOutlined />} />
                    <div>
                        <Title level={3} style={{ margin: 0 }}>Ajustes</Title>
                        <Text type="secondary">Plan activo de {organizationDisplayName}</Text>
                    </div>
                    <Tag color={contract?.status === 'ACTIVO' ? 'green' : 'default'}>{contract?.status || 'No disponible'}</Tag>
                </Space>
            </Card>
            <Card title="Plan activo">
                {contract ? <Descriptions column={{ xs: 1, sm: 2 }}>
                    <Descriptions.Item label="Plan">{pick(contract, 'name', 'Name') || 'No disponible'}</Descriptions.Item>
                    <Descriptions.Item label="Estado">{pick(contract, 'status', 'Status') || 'No disponible'}</Descriptions.Item>
                    <Descriptions.Item label="Días restantes">{pick(contract, 'daysLeft', 'DaysLeft') ?? 'No disponible'}</Descriptions.Item>
                    <Descriptions.Item label="Válido hasta">{formatDate(pick(contract, 'activeUntil', 'ActiveUntil'))}</Descriptions.Item>
                </Descriptions> : <Empty description="No existe un plan activo para esta organización." />}
            </Card>
            {contract && <Card title="Características">
                <Row gutter={[16, 16]}>
                    {[
                        ['Especialidades', pick(flags, 'maxSpecialities', 'MaxSpecialities')],
                        ['Pacientes', pick(flags, 'maxPatients', 'MaxPatients')],
                        ['Citas', pick(flags, 'maxAppointments', 'MaxAppointments')],
                        ['Atenciones', pick(flags, 'maxAttentions', 'MaxAttentions')],
                        ['Emails', pick(flags, 'maxEmails', 'MaxEmails')]
                    ].map(([label, value]) => <Col xs={12} sm={8} md={4} key={label as string}><Statistic title={label as string} value={value ?? 'Ilimitado'} /></Col>)}
                </Row>
                <Divider orientation="left">Capacidad por rol</Divider>
                <Row gutter={[16, 16]}>
                    {(contractRoles || []).map(role => {
                        const roleName = pick(role, 'rolName', 'RolName') || pick(role, 'name', 'Name') || 'Rol';
                        const maxUsers = pick(role, 'maxUsers', 'MaxUsers');
                            const includedUsers = Number(pick(role, 'includedUsers', 'IncludedUsers') || 0);
                            const additionalLicensePrice = Number(pick(role, 'additionalLicensePrice', 'AdditionalLicensePrice') || 0);
                        return <Col xs={12} sm={8} md={6} key={String(pick(role, 'rolSystemId', 'RolSystemId'))}>
                                <Statistic title={roleName} value={maxUsers === 0 || maxUsers == null ? 'Ilimitado' : maxUsers} />
                                <Text type="secondary">
                                    Incluidas: {includedUsers} · Adicional: ${additionalLicensePrice.toFixed(2)}
                                </Text>
                        </Col>;
                    })}
                </Row>
            </Card>}
        </Space>
    );

    if (loading) return <Card><Spin /></Card>;

    return <Tabs
        defaultActiveKey="images"
        items={[
            { key: 'images', label: 'Imágenes', children: organizationAssetsSection },
            { key: 'medics', label: 'Médicos y horarios', children: scheduleSection },
            { key: 'centers', label: 'Sucursales', children: <OrganizationCentersManagement organizationId={organizationId} /> },
            { key: 'specialities', label: 'Especialidades', children: <OrganizationSpecialityManagement organizationId={organizationId} organizationName={organizationDisplayName} maxSpecialities={pick(flags, 'maxSpecialities', 'MaxSpecialities')} /> },
            { key: 'center-specialities', label: 'Especialidades por sucursal', children: <OrganizationCenterSpecialityManagement organizationId={organizationId} /> },
            { key: 'global-configuration', label: 'Configuracion global', children: <GlobalOrganizationConfiguration /> },
            { key: 'business-coverage', label: 'Empresas y coberturas', children: <OrganizationBusinessCoverageManagement organizationId={organizationId} /> },
            { key: 'plan', label: 'Plan activo', children: planSection }
        ]}
    />;
};

export default connect(null, {
    deleteMedicSchedule,
    deleteScheduleException,
    getMedicScheduleByUser,
    getOrganizationManagementCenters,
    getOrganizationManagementPlan,
    getOrganizationMedics,
    getReasonsScheduleExceptions,
    getWeekDays,
    postMedicSchedule,
    postScheduleException,
    putOrganization,
    putMedicSchedule,
    putScheduleException
})(OrganizationSettingsManagement);
